"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Button, Section, DropdownMenu } from "@/src/components";
import { DataTable, createSelectColumn } from "@/src/components/information/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, ArrowUpDown } from "lucide-react";

type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "active" | "inactive" | "pending";
};

const sampleData: User[] = [
  { id: "1", name: "John Doe", email: "john@example.com", role: "Admin", status: "active" },
  { id: "2", name: "Jane Smith", email: "jane@example.com", role: "Editor", status: "active" },
  { id: "3", name: "Bob Wilson", email: "bob@example.com", role: "Viewer", status: "inactive" },
  { id: "4", name: "Alice Brown", email: "alice@example.com", role: "Editor", status: "pending" },
  { id: "5", name: "Charlie Davis", email: "charlie@example.com", role: "Admin", status: "active" },
];

const columns: ColumnDef<User>[] = [
  createSelectColumn<User>(),
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="outline"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Name
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
  },
  {
    accessorKey: "email",
    header: "Email",
  },
  {
    accessorKey: "role",
    header: "Role",
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status;
      const variant = status === "active" ? "default" : status === "pending" ? "secondary" : "outline";
      return <Badge variant={variant}>{status}</Badge>;
    },
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <DropdownMenu
        trigger={{
          icon: <MoreHorizontal className="h-4 w-4" />,
          iconOnly: true,
          variant: "ghost",
        }}
        items={[
          { type: "item", label: "Edit", onClick: () => console.log("Edit", row.original.id) },
          { type: "item", label: "View", onClick: () => console.log("View", row.original.id) },
          { type: "separator" },
          { type: "item", label: "Delete", onClick: () => console.log("Delete", row.original.id) },
        ]}
      />
    ),
  },
];

export default function DocsBlocksDataTable() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            DataTable
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A feature-rich data table powered by TanStack Table with sorting,
            filtering, pagination, and row selection.
          </Text>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <SectionCard
        header={<CardTitle>Features</CardTitle>}
        contentClassName="p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">✓</Badge>
            <Text className="text-sm">Sorting</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">✓</Badge>
            <Text className="text-sm">Filtering</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">✓</Badge>
            <Text className="text-sm">Pagination</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">✓</Badge>
            <Text className="text-sm">Row Selection</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">✓</Badge>
            <Text className="text-sm">Column Visibility</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">✓</Badge>
            <Text className="text-sm">Empty State</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Live Demo */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Live Demo</CardTitle>
            <CardDescription>Interactive example with all features enabled</CardDescription>
          </>
        }
        contentClassName="p-2"
      >
        <DataTable
          columns={columns}
          data={sampleData}
          filterColumn="name"
          filterPlaceholder="Filter by name..."
        />
      </SectionCard>
      </Section>

      {/* Basic Usage */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Basic Usage</CardTitle>
            <CardDescription>Minimum code to get started</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
          <code className="block">{"import { DataTable, createSelectColumn } from \"@/registry/new-york/blocks/data-table/data-table\";"}</code>
          <code className="block">{"import { ColumnDef } from \"@tanstack/react-table\";"}</code>
          <code className="block mt-4">{"type User = {"}</code>
          <code className="block pl-4">{"id: string;"}</code>
          <code className="block pl-4">{"name: string;"}</code>
          <code className="block pl-4">{"email: string;"}</code>
          <code className="block">{"};"}</code>
          <code className="block mt-4">{"const columns: ColumnDef<User>[] = ["}</code>
          <code className="block pl-4">{"createSelectColumn<User>(),"}</code>
          <code className="block pl-4">{"{ accessorKey: \"name\", header: \"Name\" },"}</code>
          <code className="block pl-4">{"{ accessorKey: \"email\", header: \"Email\" },"}</code>
          <code className="block">{"];"}</code>
          <code className="block mt-4">{"<DataTable"}</code>
          <code className="block pl-4">{"columns={columns}"}</code>
          <code className="block pl-4">{"data={users}"}</code>
          <code className="block pl-4">{'filterColumn="name"'}</code>
          <code className="block pl-4">{'filterPlaceholder="Search users..."'}</code>
          <code className="block">{"/>"}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Custom Cell Rendering */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Custom Cell Rendering</CardTitle>
            <CardDescription>Render custom content in table cells</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
          <code className="block">{"{"}</code>
          <code className="block pl-4">{"accessorKey: \"status\","}</code>
          <code className="block pl-4">{"header: \"Status\","}</code>
          <code className="block pl-4">{"cell: ({ row }) => {"}</code>
          <code className="block pl-8">{"const status = row.original.status;"}</code>
          <code className="block pl-8">{"return <Badge variant={status === \"active\" ? \"default\" : \"secondary\"}>{status}</Badge>;"}</code>
          <code className="block pl-4">{"},"}</code>
          <code className="block">{"}"}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionCard
        header={<CardTitle>Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Default</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">columns</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ColumnDef[]</td>
              <td className="py-2 font-mono text-xs">required</td>
              <td className="py-2 text-muted-foreground">Column definitions</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">data</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">TData[]</td>
              <td className="py-2 font-mono text-xs">required</td>
              <td className="py-2 text-muted-foreground">Table data array</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">filterColumn</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Column to filter on</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">filterPlaceholder</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
              <td className="py-2 font-mono text-xs">"Filter..."</td>
              <td className="py-2 text-muted-foreground">Filter input placeholder</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">showToolbar</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">true</td>
              <td className="py-2 text-muted-foreground">Show filter and column toggle</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">showFooter</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">true</td>
              <td className="py-2 text-muted-foreground">Show pagination footer</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">emptyState</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Custom empty state content</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
        header={<CardTitle>Accessibility</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <ul className="space-y-2 text-sm">
          <li>• <strong>Semantic table:</strong> Uses proper table, thead, tbody, th, td elements</li>
          <li>• <strong>Sortable columns:</strong> Sort state indicated with aria-sort</li>
          <li>• <strong>Keyboard navigation:</strong> Tab through focusable elements</li>
          <li>• <strong>Selection state:</strong> Checkbox states announced to screen readers</li>
          <li>• <strong>Pagination:</strong> Navigation buttons have proper labels</li>
        </ul>
      </SectionCard>
      </Section>
    </>
  );
}
